import { useState } from 'react';
import { useOIDCDebugger } from '../../Utilities/UseOIDCDebugger';

interface OIDCDebugPanelProps {
  /** Whether to show the debug panel by default */
  defaultExpanded?: boolean;
  /** Custom CSS class for styling */
  className?: string;
  /** Whether to show in production environments */
  showInProduction?: boolean;
}

/**
 * JWT Authentication Debug Panel Component
 *
 * A React component that provides debugging functionality for the JWT cookie
 * authentication system. This panel helps troubleshoot JWT token generation,
 * cookie setting, and API authentication issues.
 *
 * Usage:
 * - Debug JWT cookie authentication in all environments
 * - Test API endpoints that require JWT authentication
 * - Troubleshoot authentication flow issues
 * - Can be temporarily added to any page for debugging
 */

/**
 * Authentication Debug Panel Component
 *
 * MIGRATION NOTICE (June 2025):
 * With the JWT cookie migration complete, this panel now primarily serves as a
 * legacy debugging tool for OIDC header issues. In most cases, JWT cookies handle
 * authentication automatically without client-side intervention.
 *
 * A React component that provides an easy-to-use interface for debugging
 * authentication issues, including both legacy OIDC and current JWT cookie flows.
 *
 * Usage:
 * - Use primarily for debugging preview environments where OIDC headers are injected
 * - JWT cookie authentication works automatically in production
 * - Can be temporarily added to any page for troubleshooting
 */
export function JWTAuthDebugPanel({
  defaultExpanded = false,
  className = '',
  showInProduction = false,
}: OIDCDebugPanelProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const {
    isRunning,
    report,
    error,
    runDebugCheck,
    clearReport,
    formattedReport,
  } = useOIDCDebugger();

  // Hide in production unless explicitly allowed
  if (!showInProduction && !process.env.NODE_ENV?.includes('dev')) {
    return null;
  }

  const hasJWTCookie = () => {
    if (typeof document === 'undefined') return false;
    return document.cookie.includes('jwt_token=');
  };

  const getEnvironmentType = () => {
    if (typeof window === 'undefined') return 'Unknown';
    const hostname = window.location.hostname;

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return 'Development';
    } else if (hostname.includes('test')) {
      return 'Test';
    } else if (hostname.includes('sandbox')) {
      return 'Sandbox';
    } else if (hostname.includes('preview')) {
      return 'Preview';
    } else {
      return 'Production';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'border-red-500 bg-red-50';
      case 'warning':
        return 'border-yellow-500 bg-yellow-50';
      default:
        return 'border-green-500 bg-green-50';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return '🔴';
      case 'warning':
        return '🟡';
      default:
        return '🟢';
    }
  };

  return (
    <div
      className={`fixed bottom-4 right-4 max-w-sm z-50${className ? ` ${className}` : ''}`}
      data-testid="oidc-debug-panel"
      data-default-expanded={defaultExpanded.toString()}
      data-show-in-production={showInProduction.toString()}
    >
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg">
        {/* Collapsed Header - Small and Unobtrusive */}
        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            className="px-3 py-2 text-left bg-blue-800 hover:bg-blue-900 rounded-lg border border-blue-800 font-medium text-white flex items-center gap-2 text-sm shadow-md"
            style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            title="Open JWT Auth Debug Panel"
          >
            🔍
            <span className="text-xs bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded font-semibold">
              JWT
            </span>
          </button>
        )}

        {/* Expanded Header */}
        {isExpanded && (
          <button
            onClick={() => setIsExpanded(false)}
            className="w-full px-4 py-2 text-left bg-blue-800 hover:bg-blue-900 rounded-t-lg border-b border-blue-700 font-medium text-white flex items-center justify-between"
            style={{ backgroundColor: '#1e3a8a', color: 'white' }}
          >
            <span className="flex items-center gap-2">
              🔍 JWT Auth Debug Panel
              <span className="text-xs bg-blue-200 text-blue-900 px-2 py-1 rounded font-semibold">
                JWT
              </span>
            </span>
            <span className="text-blue-200 text-sm font-bold">×</span>
          </button>
        )}

        {/* Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            {/* Environment Info */}
            <div className="text-sm text-gray-600">
              <div>
                <strong>Environment:</strong> {getEnvironmentType()}
              </div>
              <div>
                <strong>Hostname:</strong> {window.location.hostname}
              </div>
              <div>
                <strong>JWT Cookie:</strong>{' '}
                {hasJWTCookie() ? '✅ Present' : '❌ Missing'}
              </div>
              <div>
                <strong>Auth Method:</strong> JWT Cookies
              </div>
            </div>

            {/* Controls */}
            <div className="flex gap-2">
              <button
                onClick={runDebugCheck}
                disabled={isRunning}
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium shadow-sm border border-blue-600"
                style={{
                  color: 'white',
                  backgroundColor: isRunning ? '#9CA3AF' : '#2563EB',
                }}
              >
                {isRunning ? '🔄 Running...' : '🧪 Test JWT Auth'}
              </button>

              {(report || error) && (
                <button
                  onClick={clearReport}
                  className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-medium shadow-sm border border-gray-600"
                  style={{ color: 'white', backgroundColor: '#4B5563' }}
                >
                  🗑️ Clear
                </button>
              )}
            </div>

            {/* Loading State */}
            {isRunning && (
              <div className="text-center text-gray-600 text-sm">
                <div className="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                Testing JWT authentication and collecting debug info...
                <div className="text-xs mt-1 text-gray-500">
                  Checking JWT cookies and API endpoints
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
                <div className="font-medium">❌ Debug Check Failed</div>
                <div className="mt-1">{error}</div>
              </div>
            )}

            {/* Report Summary */}
            {report && (
              <div
                className={`p-3 border rounded ${getSeverityColor(report.severity)}`}
              >
                <div className="font-medium text-sm mb-2">
                  {getSeverityIcon(report.severity)} Debug Report Summary
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <strong>Tests Run:</strong> {report.endpointTests.length}
                  </div>

                  {report.endpointTests.map((test: any, index: number) => (
                    <div key={index} className="ml-2">
                      <div className="flex items-center gap-2">
                        <span>
                          {test.status >= 200 && test.status < 300
                            ? '✅'
                            : test.status >= 300 && test.status < 400
                              ? '🔄'
                              : test.status >= 400
                                ? '❌'
                                : '⚠️'}
                        </span>
                        <span className="font-mono text-xs">
                          {test.method} {test.endpoint.split('/').pop()}
                        </span>
                        <span className="text-gray-600">({test.status})</span>
                      </div>
                    </div>
                  ))}

                  {report.recommendations.length > 0 && (
                    <div className="mt-3">
                      <div className="font-medium mb-1">Recommendations:</div>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        {report.recommendations
                          .slice(0, 3)
                          .map((rec: string, index: number) => (
                            <li key={index}>{rec}</li>
                          ))}
                        {report.recommendations.length > 3 && (
                          <li className="text-gray-600">
                            ... and {report.recommendations.length - 3} more
                            (see console)
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Full Report Toggle */}
            {formattedReport && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                  📋 View Full Report
                </summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-64 whitespace-pre-wrap font-mono">
                  {formattedReport}
                </pre>
              </details>
            )}

            {/* Quick Actions */}
            <div className="border-t pt-3 text-xs text-gray-600 space-y-1">
              <div>
                💡 <strong>Tips:</strong>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>JWT cookies are set automatically when you log in</li>
                <li>Refresh the page if JWT cookie is missing after login</li>
                <li>302 redirects are normal for unauthenticated users</li>
                <li>Check browser console for detailed error logs</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Simple debug button that can be added anywhere for quick debugging
 *
 * MIGRATION NOTICE: Now primarily tests JWT cookie authentication flow
 */
export function OIDCDebugButton({ className = '' }: { className?: string }) {
  const { runDebugCheck, isRunning } = useOIDCDebugger();

  const handleDebug = () => {
    const result = runDebugCheck();
    // Ensure result is a Promise before calling .then()
    if (result && typeof result.then === 'function') {
      result.then(() => {
        // The hook will log to console, but also show a notification
        console.log(
          '🔍 Auth debug check completed. Check console for details.',
        );
      });
    } else {
      // Fallback for non-Promise returns (shouldn't happen in normal usage)
      console.log('🔍 Auth debug check completed. Check console for details.');
    }
  };

  return (
    <button
      onClick={handleDebug}
      disabled={isRunning}
      className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm border border-blue-600 ${className}`}
      style={{
        color: 'white',
        backgroundColor: isRunning ? '#9CA3AF' : '#2563EB',
      }}
      title="Run authentication debug check and log results to console"
    >
      {isRunning ? '🔄 Debugging...' : '🔍 Debug Auth'}
    </button>
  );
}

// Backwards compatibility exports
export const OIDCDebugPanel = JWTAuthDebugPanel;
